import React, { useState, useEffect, useMemo } from 'react';
import { User } from '@/api/entities';
import { useUser, useCurrentTenant } from '@/contexts/UserContext';
import { resolveEffectiveTenant } from '@/lib/tenantUtils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/components/use-toast';
import { Plus, Trash2, Save, Loader2, Calendar, CalendarDays, ChevronDown, ChevronUp, Sparkles, Link as LinkIcon, Check } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useTranslation } from '@/components/utils/translations';
import EventCustomPlaybookStatusBanner from '@/components/events/EventCustomPlaybookStatusBanner';

const API_BASE_URL = window.location.hostname === 'localhost' 
  ? 'https://api.neogtm.com' 
  : 'https://api.neogtm.com';

const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'Europe/London',
  'Europe/Paris',
  'Asia/Tokyo',
  'Australia/Sydney'
];

const CHANNELS = [
  { value: 'email', label: 'Email' },
  { value: 'linkedin', label: 'LinkedIn' },
];

/** YYYY-MM-DD (local) for `<input type="date" min>` and validation. */
function todayDateInputMin() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function eventDateInputValue(iso) {
  return String(iso ?? '').split('T')[0] || '';
}

const normalizeAssetOptions = (items, defaultType = 'link', idPrefix = 'asset') => {
  if (!Array.isArray(items)) return [];
  return items
    .map((item, index) => {
      if (typeof item === 'string') {
        return {
          id: `${idPrefix}_${index}`,
          label: item,
          url: item,
          type: defaultType,
        };
      }
      if (!item || typeof item !== 'object') return null;
      const id = String(item.id ?? item.key ?? item.value ?? item.url ?? `${idPrefix}_${index}`);
      const label = String(item.label ?? item.name ?? item.title ?? item.url ?? id);
      return {
        id,
        label,
        url: String(item.url ?? ''),
        type: item.type ?? defaultType,
      };
    })
    .filter(Boolean);
};

// Default touch templates (legacy flat list)
const DEFAULT_TOUCHES = [
  { order: 1, day_offset: -14, channel: 'email', goal: 'Initial outreach - introduce yourself and ask if attending', cta_type: '', collateral: [], messaging_notes: '', include_signals: true },
  { order: 2, day_offset: -7, channel: 'linkedin', goal: 'Build connection - share relevant content', cta_type: '', collateral: [], messaging_notes: '', include_signals: true },
  { order: 3, day_offset: -3, channel: 'email', goal: 'Confirm meeting - share booth/session details', cta_type: '', collateral: [], messaging_notes: '', include_signals: true },
  { order: 4, day_offset: 0, channel: 'email', goal: 'Day-of reminder - exact location and time', cta_type: '', collateral: [], messaging_notes: '', include_signals: false },
  { order: 5, day_offset: 1, channel: 'email', goal: 'Follow-up - thank you and next steps', cta_type: '', collateral: [], messaging_notes: '', include_signals: true },
];

const EVENT_PLAYBOOK_TYPES = [
  { key: 'event_booth_meeting_v1', label: 'Booth Meeting', defaultCount: 5 },
  { key: 'event_session_invite_v1', label: 'Session Invite', defaultCount: 4 },
  { key: 'event_vip_outreach_v1', label: 'VIP Outreach', defaultCount: 6 },
  { key: 'event_quick_connect_v1', label: 'Quick Connect', defaultCount: 3 },
];

function normalizeTouchForEditor(touch) {
  return {
    ...touch,
    cta_type: touch?.cta_type ?? '',
    collateral: touch?.collateral ?? [],
    messaging_notes: touch?.messaging_notes ?? '',
    include_signals: touch?.include_signals ?? true,
    include_availability_link:
      touch?.include_availability_link ??
      touch?.touch_config?.include_availability_link ??
      false,
  };
}

function normalizeTouchesList(touches) {
  return (touches || []).map(normalizeTouchForEditor);
}

function normalizeTouchesByTypeMap(byType) {
  if (!byType || typeof byType !== 'object' || Array.isArray(byType)) return {};
  const out = {};
  for (const [key, touches] of Object.entries(byType)) {
    if (!Array.isArray(touches)) continue;
    out[key] = normalizeTouchesList(touches);
  }
  return out;
}

function normalizeTouchForSave(touch) {
  const includeAvailabilityLink =
    touch?.include_availability_link ??
    touch?.touch_config?.include_availability_link ??
    false;
  return {
    order: touch.order,
    day_offset: touch.day_offset,
    channel: touch.channel,
    goal: touch.goal ?? '',
    cta_type: touch.cta_type ?? '',
    collateral: touch.collateral ?? [],
    messaging_notes: touch.messaging_notes ?? '',
    include_signals: !!touch.include_signals,
    include_availability_link: includeAvailabilityLink,
    touch_config: {
      ...(touch?.touch_config || {}),
      include_availability_link: includeAvailabilityLink,
    },
  };
}

function serializeTouchesForSave(touches) {
  return (touches || []).map(normalizeTouchForSave);
}

function serializeTouchesByTypeForSave(byType) {
  const src = byType && typeof byType === 'object' && !Array.isArray(byType) ? byType : {};
  const out = {};
  for (const { key } of EVENT_PLAYBOOK_TYPES) {
    const touches = src[key];
    if (Array.isArray(touches) && touches.length > 0) {
      out[key] = serializeTouchesForSave(touches);
    }
  }
  for (const [key, touches] of Object.entries(src)) {
    if (out[key] || !Array.isArray(touches) || touches.length === 0) continue;
    out[key] = serializeTouchesForSave(touches);
  }
  return out;
}

function buildDefaultTouchesForType(defaultCount) {
  const count = Math.max(1, defaultCount);
  if (count <= DEFAULT_TOUCHES.length) {
    return DEFAULT_TOUCHES.slice(0, count).map((t, i) => ({
      ...normalizeTouchForEditor(t),
      order: i + 1,
    }));
  }
  const out = DEFAULT_TOUCHES.map((t, i) => ({
    ...normalizeTouchForEditor(t),
    order: i + 1,
  }));
  let lastOffset = DEFAULT_TOUCHES[DEFAULT_TOUCHES.length - 1].day_offset;
  for (let i = DEFAULT_TOUCHES.length; i < count; i += 1) {
    lastOffset += 3;
    out.push(
      normalizeTouchForEditor({
        order: i + 1,
        day_offset: lastOffset,
        channel: 'email',
        goal: '',
        cta_type: '',
        collateral: [],
        messaging_notes: '',
        include_signals: true,
      }),
    );
  }
  return out;
}

const COUNTRIES = [
  { value: 'Afghanistan', label: 'Afghanistan' },
  { value: 'Albania', label: 'Albania' },
  { value: 'Algeria', label: 'Algeria' },
  { value: 'Argentina', label: 'Argentina' },
  { value: 'Armenia', label: 'Armenia' },
  { value: 'Australia', label: 'Australia' },
  { value: 'Austria', label: 'Austria' },
  { value: 'Azerbaijan', label: 'Azerbaijan' },
  { value: 'Bangladesh', label: 'Bangladesh' },
  { value: 'Belgium', label: 'Belgium' },
  { value: 'Brazil', label: 'Brazil' },
  { value: 'Bulgaria', label: 'Bulgaria' },
  { value: 'Canada', label: 'Canada' },
  { value: 'Chile', label: 'Chile' },
  { value: 'China', label: 'China' },
  { value: 'Colombia', label: 'Colombia' },
  { value: 'Congo', label: 'Congo' },
  { value: 'Croatia', label: 'Croatia' },
  { value: 'Cyprus', label: 'Cyprus' },
  { value: 'Czech Republic', label: 'Czech Republic' },
  { value: 'Denmark', label: 'Denmark' },
  { value: 'Dominican Republic', label: 'Dominican Republic' },
  { value: 'Egypt', label: 'Egypt' },
  { value: 'Ethiopia', label: 'Ethiopia' },
  { value: 'Finland', label: 'Finland' },
  { value: 'France', label: 'France' },
  { value: 'Georgia', label: 'Georgia' },
  { value: 'Germany', label: 'Germany' },
  { value: 'Ghana', label: 'Ghana' },
  { value: 'Greece', label: 'Greece' },
  { value: 'Guernsey', label: 'Guernsey' },
  { value: 'Guinea', label: 'Guinea' },
  { value: 'Hong Kong', label: 'Hong Kong' },
  { value: 'Hungary', label: 'Hungary' },
  { value: 'Iceland', label: 'Iceland' },
  { value: 'India', label: 'India' },
  { value: 'Indonesia', label: 'Indonesia' },
  { value: 'Iran', label: 'Iran' },
  { value: 'Iraq', label: 'Iraq' },
  { value: 'Ireland', label: 'Ireland' },
  { value: 'Israel', label: 'Israel' },
  { value: 'Italy', label: 'Italy' },
  { value: 'Japan', label: 'Japan' },
  { value: 'Jordan', label: 'Jordan' },
  { value: 'Kazakhstan', label: 'Kazakhstan' },
  { value: 'Kenya', label: 'Kenya' },
  { value: 'Kosovo', label: 'Kosovo' },
  { value: 'Latvia', label: 'Latvia' },
  { value: 'Lebanon', label: 'Lebanon' },
  { value: 'Lithuania', label: 'Lithuania' },
  { value: 'Luxembourg', label: 'Luxembourg' },
  { value: 'Malaysia', label: 'Malaysia' },
  { value: 'Malta', label: 'Malta' },
  { value: 'Mexico', label: 'Mexico' },
  { value: 'Moldova', label: 'Moldova' },
  { value: 'Mongolia', label: 'Mongolia' },
  { value: 'Morocco', label: 'Morocco' },
  { value: 'Netherlands', label: 'Netherlands' },
  { value: 'New Zealand', label: 'New Zealand' },
  { value: 'Nigeria', label: 'Nigeria' },
  { value: 'North Macedonia', label: 'North Macedonia' },
  { value: 'Norway', label: 'Norway' },
  { value: 'Pakistan', label: 'Pakistan' },
  { value: 'Peru', label: 'Peru' },
  { value: 'Philippines', label: 'Philippines' },
  { value: 'Poland', label: 'Poland' },
  { value: 'Portugal', label: 'Portugal' },
  { value: 'Qatar', label: 'Qatar' },
  { value: 'Romania', label: 'Romania' },
  { value: 'Russia', label: 'Russia' },
  { value: 'Saudi Arabia', label: 'Saudi Arabia' },
  { value: 'Sierra Leone', label: 'Sierra Leone' },
  { value: 'Singapore', label: 'Singapore' },
  { value: 'Slovakia', label: 'Slovakia' },
  { value: 'Slovenia', label: 'Slovenia' },
  { value: 'South Africa', label: 'South Africa' },
  { value: 'South Korea', label: 'South Korea' },
  { value: 'Spain', label: 'Spain' },
  { value: 'Sri Lanka', label: 'Sri Lanka' },
  { value: 'Sweden', label: 'Sweden' },
  { value: 'Switzerland', label: 'Switzerland' },
  { value: 'Taiwan', label: 'Taiwan' },
  { value: 'Tajikistan', label: 'Tajikistan' },
  { value: 'Tanzania', label: 'Tanzania' },
  { value: 'Thailand', label: 'Thailand' },
  { value: 'Turkey', label: 'Turkey' },
  { value: 'Uganda', label: 'Uganda' },
  { value: 'Ukraine', label: 'Ukraine' },
  { value: 'United Arab Emirates', label: 'United Arab Emirates' },
  { value: 'United Kingdom', label: 'United Kingdom' },
  { value: 'United States', label: 'United States' },
  { value: 'Uruguay', label: 'Uruguay' },
  { value: 'Uzbekistan', label: 'Uzbekistan' },
  { value: 'Vietnam', label: 'Vietnam' }
];

const SORTED_COUNTRIES = [...COUNTRIES].sort((a, b) =>
  a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
);

// Helper to check if "ALL" mode is active (no country restrictions)
const isAllCountriesMode = (countries) => {
  return Array.isArray(countries) && countries.includes('ALL');
};

// Helper to convert API event format to internal format for country selection
const convertEventCountryFormat = (event) => {
  // If target_countries is ['ALL'] or country is 'ALL', convert to internal format
  if (event.target_countries && event.target_countries.includes('ALL')) {
    return { ...event, country: ['ALL'] };
  }
  if (event.country === 'ALL') {
    return { ...event, country: ['ALL'] };
  }
  // If target_countries exists, use it; otherwise use country field
  if (event.target_countries && Array.isArray(event.target_countries)) {
    return { ...event, country: event.target_countries };
  }
  // If country is a string, convert to array
  if (event.country && !Array.isArray(event.country)) {
    return { ...event, country: [event.country] };
  }
  return event;
};

function enrichEventFromApi(event) {
  return {
    ...convertEventCountryFormat(event),
    custom_touches: normalizeTouchesList(event.custom_touches),
    custom_touches_by_type: normalizeTouchesByTypeMap(event.custom_touches_by_type),
    use_custom_playbook: event.use_custom_playbook || false,
    // [2026-10-05] Default OFF so an event saved before this column existed keeps
    // today's behaviour (English for every recipient).
    use_native_language: event.use_native_language === true,
  };
}

// Touch Editor Component
function TouchEditor({ touch, index, onUpdate, onRemove, onMoveUp, onMoveDown, isFirst, isLast, eventStartDate, productLinkOptions, hasAvailabilityLink, t }) {
  const [isOpen, setIsOpen] = useState(false);
  
  const getPhaseLabel = (dayOffset) => {
    if (dayOffset < 0) return t('preEvent');
    if (dayOffset === 0) return t('dayOf');
    return t('postEvent');
  };
  
  const getScheduledDate = () => {
    if (!eventStartDate) return null;
    const date = new Date(eventStartDate);
    date.setDate(date.getDate() + touch.day_offset);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const selectedCollateralIds = (touch.collateral || [])
    .map((item) => String(item?.id || ''))
    .filter(Boolean);

  const toggleCollateral = (option) => {
    const current = touch.collateral || [];
    const exists = current.some((item) => String(item?.id || '') === option.id);
    if (exists) {
      onUpdate(
        'collateral',
        current.filter((item) => String(item?.id || '') !== option.id)
      );
      return;
    }
    onUpdate('collateral', [
      ...current,
      { id: option.id, name: option.label, url: option.url, type: option.type || 'link' },
    ]);
  };

  return (
    <Card className="border border-gray-200">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50">
            <div className="flex items-center gap-3">
              <div className="flex flex-col gap-1">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-5 w-5"
                  onClick={(e) => { e.stopPropagation(); onMoveUp(); }}
                  disabled={isFirst}
                >
                  <ChevronUp className="h-3 w-3" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-5 w-5"
                  onClick={(e) => { e.stopPropagation(); onMoveDown(); }}
                  disabled={isLast}
                >
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <span className="bg-[#00C2C2] text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                  {index + 1}
                </span>
                <div>
                  <div className="font-medium text-sm">
                    {t('dayLabel')} {touch.day_offset >= 0 ? '+' : ''}{touch.day_offset} • {touch.channel === 'email' ? '📧' : '💼'} {touch.channel}
                    <span className="ml-2 text-xs text-gray-500">({getPhaseLabel(touch.day_offset)})</span>
                  </div>
                  <div className="text-xs text-gray-500 truncate max-w-[300px]">
                    {touch.goal || t('noGoalSet')}
                    {getScheduledDate() && <span className="ml-2">• {getScheduledDate()}</span>}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {touch.collateral?.length > 0 && (
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                  {touch.collateral.length} {t('asset')}{touch.collateral.length > 1 ? 's' : ''}
                </span>
              )}
              {touch.include_signals && (
                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded flex items-center gap-1">
                  <Sparkles className="h-3 w-3" /> {t('signals')}
                </span>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => { e.stopPropagation(); onRemove(); }}
                className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
              {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </div>
          </div>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <div className="px-4 pb-4 space-y-4 border-t pt-4">
            {/* Basic Settings */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>{t('dayOffset')}</Label>
                <Input
                  type="number"
                  value={touch.day_offset}
                  onChange={(e) => onUpdate('day_offset', parseInt(e.target.value))}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">{t('negativeBeforeEvent')}</p>
              </div>
              <div>
                <Label>{t('channel')}</Label>
                <Select 
                  value={touch.channel} 
                  onValueChange={(value) => onUpdate('channel', value)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CHANNELS.map(ch => (
                      <SelectItem key={ch.value} value={ch.value}>{ch.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch
                  checked={touch.include_signals}
                  onCheckedChange={(checked) => onUpdate('include_signals', checked)}
                />
                <Label className="text-sm">{t('useAiSignals')}</Label>
              </div>
            </div>


            {touch.channel === 'email' && (
              <div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={!!touch.include_availability_link}
                    onCheckedChange={(checked) => onUpdate('include_availability_link', checked)}
                    disabled={!hasAvailabilityLink}
                  />
                  <Label className="text-sm">{t('includeBookingLink')}</Label>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {t('autoInsertAvailabilityLink')}
                </p>
                {!hasAvailabilityLink && (
                  <p className="text-xs text-amber-700 mt-1">
                    {t('addAvailabilityLinkToEnable')}
                  </p>
                )}
              </div>
            )}

            {/* Goal */}
            <div>
              <Label>{t('touchGoal')}</Label>
              <Input
                placeholder={t('touchGoalPlaceholder')}
                value={touch.goal || ''}
                onChange={(e) => onUpdate('goal', e.target.value)}
                className="mt-1"
              />
            </div>

            {/* Messaging Notes */}
            <div>
              <Label>{t('messagingNotes')}</Label>
              <Textarea
                placeholder={t('messagingNotesPlaceholder')}
                value={touch.messaging_notes || ''}
                onChange={(e) => onUpdate('messaging_notes', e.target.value)}
                rows={3}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('instructionsPassedToAiTouch')}
              </p>
            </div>

            {/* Collateral */}
            <div>
              <Label>{t('collateralToShare')}</Label>
              {(productLinkOptions || []).length === 0 ? (
                <div className="text-center py-4 text-gray-500 text-sm border border-dashed rounded-md mt-2">
                  {t('noAssetsConfiguredInPersonalization')}
                </div>
              ) : (
                <div className="border rounded-md p-2 mt-2 space-y-1 max-h-40 overflow-y-auto">
                  {productLinkOptions.map((option) => {
                    const isChecked = selectedCollateralIds.includes(option.id);
                    return (
                      <label
                        key={option.id}
                        className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 rounded px-1 py-1"
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleCollateral(option)}
                          className="w-4 h-4 rounded border-gray-300"
                        />
                        <LinkIcon className="h-3.5 w-3.5 text-gray-500" />
                        <span className="text-sm">
                          {option.label}
                          {option.type === 'case_study' ? ` (${t('caseStudy')})` : ''}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
              {(touch.collateral || []).length > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  {(touch.collateral || []).length} {t('asset')}{(touch.collateral || []).length !== 1 ? 's' : ''} {t('selected')}
                </p>
              )}
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

function getInitialSelectedPlaybookType(byType) {
  const configured = EVENT_PLAYBOOK_TYPES.find((pt) => (byType?.[pt.key] || []).length > 0);
  return configured?.key ?? EVENT_PLAYBOOK_TYPES[0].key;
}

function EventCustomPlaybookBuilder({ event, onPatchEvent, productLinkOptions, t }) {
  const byType = event.custom_touches_by_type || {};
  const [selectedType, setSelectedType] = useState(() => getInitialSelectedPlaybookType(byType));
  const touches = byType[selectedType] || [];
  const selectedMeta = EVENT_PLAYBOOK_TYPES.find((pt) => pt.key === selectedType) || EVENT_PLAYBOOK_TYPES[0];
  const configuredTypes = EVENT_PLAYBOOK_TYPES.filter((pt) => (byType[pt.key] || []).length > 0);
  const configuredCount = configuredTypes.length;

  const setTouchesForType = (typeKey, nextTouches) => {
    onPatchEvent({
      custom_touches_by_type: {
        ...byType,
        [typeKey]: nextTouches,
      },
    });
  };

  const addTouch = () => {
    const lastTouch = touches[touches.length - 1];
    const newTouch = normalizeTouchForEditor({
      order: touches.length + 1,
      day_offset: lastTouch ? lastTouch.day_offset + 3 : -14,
      channel: 'email',
      goal: '',
      cta_type: '',
      collateral: [],
      messaging_notes: '',
      include_signals: true,
    });
    setTouchesForType(selectedType, [...touches, newTouch]);
  };

  const updateTouch = (touchIndex, field, value) => {
    const next = [...touches];
    next[touchIndex] = { ...next[touchIndex], [field]: value };
    setTouchesForType(selectedType, next);
  };

  const removeTouch = (touchIndex) => {
    const next = touches
      .filter((_, i) => i !== touchIndex)
      .map((touch, i) => ({ ...touch, order: i + 1 }));
    setTouchesForType(selectedType, next);
  };

  const moveTouch = (touchIndex, direction) => {
    const next = [...touches];
    const newIndex = touchIndex + direction;
    if (newIndex < 0 || newIndex >= next.length) return;
    [next[touchIndex], next[newIndex]] = [next[newIndex], next[touchIndex]];
    next.forEach((touch, i) => {
      touch.order = i + 1;
    });
    setTouchesForType(selectedType, next);
  };

  const startFromDefaultForType = (typeKey = selectedType) => {
    const meta = EVENT_PLAYBOOK_TYPES.find((pt) => pt.key === typeKey) || selectedMeta;
    setTouchesForType(typeKey, buildDefaultTouchesForType(meta.defaultCount));
    setSelectedType(typeKey);
  };

  const selectType = (typeKey) => {
    setSelectedType(typeKey);
  };

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-[#00C2C2]/30 bg-[#ECFFFE]/50 p-4 space-y-3">
        <div>
          <p className="text-sm font-semibold text-[#0A1A2F]">Custom sequences by playbook type</p>
          <p className="text-xs text-[#5C6B7A] mt-1 leading-relaxed">
            Booth, Session Invite, VIP, and Quick Connect each have their own touch list. Saved steps
            stay on this event until you change them — use <strong>Save</strong> at the bottom of the
            page to persist to the server.
          </p>
          {configuredCount > 0 ? (
            <p className="text-xs text-purple-800 mt-2 font-medium">
              {configuredCount} of {EVENT_PLAYBOOK_TYPES.length} types configured
              {configuredCount < EVENT_PLAYBOOK_TYPES.length
                ? ' — select a type marked “Not set up” below to add another custom sequence.'
                : ' — all types have custom steps.'}
            </p>
          ) : (
            <p className="text-xs text-amber-800 mt-2">
              No custom sequences yet. Pick a type below to create your first one.
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {EVENT_PLAYBOOK_TYPES.map((pt) => {
            const stepCount = (byType[pt.key] || []).length;
            const isConfigured = stepCount > 0;
            const isSelected = selectedType === pt.key;
            return (
              <button
                key={pt.key}
                type="button"
                onClick={() => selectType(pt.key)}
                className={`text-left rounded-lg border p-3 transition-all ${
                  isSelected
                    ? 'border-[#00C2C2] bg-white shadow-sm ring-2 ring-[#00C2C2]/40'
                    : 'border-gray-200 bg-white hover:border-[#00C2C2]/60 hover:bg-white'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="font-medium text-sm text-[#0A1A2F]">{pt.label}</span>
                  {isConfigured ? (
                    <Badge className="bg-purple-100 text-purple-800 border-purple-200 shrink-0 gap-1">
                      <Check className="h-3 w-3" />
                      {stepCount} saved
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-gray-500 shrink-0">
                      Not set up
                    </Badge>
                  )}
                </div>
                <p className="text-[11px] text-[#5C6B7A] mt-1.5">
                  Default: {pt.defaultCount} touches
                  {isConfigured ? ' · click to edit' : ' · click to add custom sequence'}
                </p>
                {!isConfigured && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[#00C2C2] mt-2">
                    <Plus className="h-3 w-3" />
                    Add custom sequence
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-3 border-t pt-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h4 className="text-sm font-semibold text-[#0A1A2F]">
            Editing: {selectedMeta.label}
          </h4>
          {touches.length > 0 && (
            <Badge variant="outline" className="bg-purple-50 text-purple-800 border-purple-200">
              {touches.length} step{touches.length === 1 ? '' : 's'} in this list
            </Badge>
          )}
        </div>

        <EventCustomPlaybookStatusBanner event={event} playbookKey={selectedType} />

        {touches.length === 0 ? (
          <div className="border border-dashed border-gray-300 rounded-lg p-6 text-center space-y-4 bg-gray-50/50">
            <p className="text-sm text-[#5C6B7A] max-w-md mx-auto">
              <strong>{selectedMeta.label}</strong> does not have a custom sequence yet. Other types
              you already configured are unchanged — set up this type separately.
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              <Button
                type="button"
                size="sm"
                className="bg-[#00C2C2] hover:bg-[#00A3A3] text-white"
                onClick={() => startFromDefaultForType(selectedType)}
              >
                Start from default ({selectedMeta.defaultCount} touches)
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={addTouch}>
                <Plus className="h-4 w-4 mr-1" />
                Add first step manually
              </Button>
            </div>
            {configuredCount > 0 && (
              <p className="text-xs text-gray-500">
                Already configured:{' '}
                {configuredTypes.map((pt) => `${pt.label} (${(byType[pt.key] || []).length})`).join(', ')}
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={() => startFromDefaultForType(selectedType)}
              >
                Replace with default ({selectedMeta.defaultCount} touches)
              </Button>
            </div>
            <div className="space-y-2">
              {touches.map((touch, touchIndex) => (
                <TouchEditor
                  key={`${selectedType}-${touchIndex}`}
                  touch={touch}
                  index={touchIndex}
                  eventStartDate={event.start_date}
                  productLinkOptions={productLinkOptions}
                  onUpdate={(field, value) => updateTouch(touchIndex, field, value)}
                  onRemove={() => removeTouch(touchIndex)}
                  onMoveUp={() => moveTouch(touchIndex, -1)}
                  onMoveDown={() => moveTouch(touchIndex, 1)}
                  isFirst={touchIndex === 0}
                  isLast={touchIndex === touches.length - 1}
                  t={t}
                  hasAvailabilityLink={!!(event.availability_link || '').trim()}
                />
              ))}
            </div>
            <Button variant="outline" size="sm" type="button" onClick={addTouch} className="w-full">
              <Plus className="h-4 w-4 mr-1" />
              {t('addTouch')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export default function SettingsEvents({ currentTenant: currentTenantProp }) {
  const { t } = useTranslation();
  const contextUser = useUser();
  const contextTenant = useCurrentTenant();
  const currentTenant = currentTenantProp || contextTenant;
  const tenantId = resolveEffectiveTenant(
    contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
    currentTenant,
    contextUser,
    'instructure'
  );
  const user = contextUser;
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [events, setEvents] = useState([]);
  const [productLinkOptions, setProductLinkOptions] = useState([]);
  const [apiAvailable, setApiAvailable] = useState(true);
  const [apiUnavailableReason, setApiUnavailableReason] = useState(null); // { status, message } | null
  const [expandedEvents, setExpandedEvents] = useState({});
  const { toast } = useToast();

  const todayMin = todayDateInputMin();

  const [countryGridCols, setCountryGridCols] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches ? 3 : 2,
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const update = () => setCountryGridCols(mq.matches ? 3 : 2);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  const countryGridRows = useMemo(
    () => Math.ceil(SORTED_COUNTRIES.length / countryGridCols),
    [countryGridCols],
  );

  const getEventErrors = (e) => {
    const errors = {};
    const startDay = eventDateInputValue(e?.start_date);
    const endDay = eventDateInputValue(e?.end_date);

    if (!(e?.event_name || e?.name || '').trim()) errors.event_name = 'Required';
    if (!(e?.city || '').trim()) errors.city = 'Required';
    if (!(e?.venue || '').trim()) errors.venue = 'Required';
    if (!e?.start_date) errors.start_date = 'Required';
    else if (startDay < todayMin) errors.start_date = 'Start date cannot be in the past';
    if (!e?.end_date) errors.end_date = 'Required';
    else if (endDay < todayMin) errors.end_date = 'End date cannot be in the past';
    else if (startDay && endDay < startDay) {
      errors.end_date = 'End date must be on or after start date';
    }
    return errors;
  };

  const hasAnyErrors = events.some((e) => Object.keys(getEventErrors(e)).length > 0);

  useEffect(() => {
    loadEvents();
    loadPersonalizationAssets();
  }, [currentTenant, contextUser]);

  const loadPersonalizationAssets = async () => {
    if (!contextUser?.id) return;
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/settings/personalization?tenant_id=${encodeURIComponent(tenantId)}&user_id=${encodeURIComponent(contextUser.id)}`,
        { credentials: 'include' }
      );
      if (!response.ok) {
        setProductLinkOptions([]);
        return;
      }
      const data = await response.json();
      const productLinks = normalizeAssetOptions(data?.product_links || [], 'link', 'product_link');
      const caseStudies = normalizeAssetOptions(data?.case_study_links || [], 'case_study', 'case_study');
      setProductLinkOptions([...productLinks, ...caseStudies]);
    } catch (error) {
      console.error('Error loading personalization assets:', error);
      setProductLinkOptions([]);
    }
  };

  const loadEvents = async () => {
    if (!contextUser) return;
    setIsLoading(true);
    try {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/settings/events?tenant_id=${tenantId}&user_id=${contextUser.id}`,
          { credentials: 'include' }
        );

        if (!response.ok) {
          // Capture status + body so the banner can explain why fallback fired.
          let bodyText = '';
          try { bodyText = await response.text(); } catch { /* ignore */ }
          const reason = { status: response.status, message: bodyText?.slice(0, 200) || response.statusText || 'Request failed' };
          setApiUnavailableReason(reason);
          setApiAvailable(false);
          throw new Error(`API ${response.status}: ${reason.message}`);
        }

        const data = await response.json();
        // Convert API format to internal format for country selection
        const convertedEvents = (data.events || []).map(enrichEventFromApi);
        setEvents(convertedEvents);
        setApiAvailable(true);
        setApiUnavailableReason(null);
      } catch (apiError) {
        console.log('Events API not available, using fallback storage:', apiError.message);
        setApiAvailable(false);
        setApiUnavailableReason(prev => prev || { status: null, message: apiError?.message || 'Network/connection error' });
        
        // Fallback to user profile
        const userEvents = contextUser.events || {};
        const tenantEvents = userEvents[tenantId] || [];
        // Convert API format to internal format for country selection
        const convertedEvents = tenantEvents.map(enrichEventFromApi);
        setEvents(convertedEvents);
      }
    } catch (error) {
      console.error('Error loading events:', error);
      toast({
        title: t('error'),
        description: t('failedToLoadEvents'),
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;

    // Block save if any event is incomplete
    for (let i = 0; i < events.length; i++) {
      const errs = getEventErrors(events[i]);
      if (Object.keys(errs).length > 0) {
        const eventLabel = events[i].event_name || events[i].name || `Event ${i + 1}`;
        const fields = Object.keys(errs).join(', ');
        toast({
          title: t('missingRequiredFields'),
          description: `${eventLabel}: ${fields}`,
          variant: 'destructive',
        });
        return;
      }
    }

    setIsSaving(true);
    try {
      // [2026-05-04] Don't permanently strand the user in fallback. Even if a prior
      // load/save flipped apiAvailable=false, attempt the API once per save click —
      // transient 5xx / cold-start / network blips shouldn't lock saves to local
      // storage for the rest of the session.
      let shouldUseFallback = false;

      if (!shouldUseFallback) {
        try {
          const response = await fetch(`${API_BASE_URL}/api/settings/events`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              user_id: user.id,
              tenant_id: tenantId,
              events: events.map(e => {
                const countries = Array.isArray(e.country) ? e.country : [];
                const isAllMode = isAllCountriesMode(countries);
                
                return {
                  id: e.id,
                  name: e.event_name || e.name,
                  city: e.city,
                  venue: e.venue,
                  start_date: e.start_date,
                  end_date: e.end_date,
                  timezone: e.timezone,
                  availability_link: e.availability_link,
                  booth_number: e.booth_number,
                  talk_link: e.talk_link,
                  pre_window_days: e.pre_window_days,
                  post_window_days: e.post_window_days,
                  priority: e.priority,
                  pre_event_guidelines: e.pre_event_guidelines,
                  during_event_guidelines: e.during_event_guidelines,
                  post_event_guidelines: e.post_event_guidelines,
                  meeting_offer: e.meeting_offer,
                  session_title: e.session_title,
                  session_description: e.session_description,
                  session_recording_link: e.session_recording_link,
                  slides_link: e.slides_link,
                  post_event_resource_link: e.post_event_resource_link,
                  // When 'ALL' is selected, pass ['ALL'] to indicate no restrictions
                  country: isAllMode ? 'ALL' : (countries[0] || null),
                  target_countries: isAllMode ? ['ALL'] : countries,
                  // New fields for custom playbook
                  use_custom_playbook: e.use_custom_playbook || false,
                  use_native_language: e.use_native_language === true,
                  custom_touches: serializeTouchesForSave(e.custom_touches),
                  custom_touches_by_type: serializeTouchesByTypeForSave(e.custom_touches_by_type),
                };
              })
            })
          });

          if (!response.ok) {
            const raw = await response.text().catch(() => '');
            let data = null;
            try {
              data = raw ? JSON.parse(raw) : null;
            } catch {
              data = null;
            }
            const apiErr = new Error(data?.reason || data?.error || 'API save failed');
            apiErr.data = data;
            throw apiErr;
          }

          toast({
            title: t('eventsSaved'),
            description: t('eventScheduleUpdated'),
          });
          setApiAvailable(true);
          setApiUnavailableReason(null);
        } catch (apiError) {
          const data = apiError?.data || {};
          if (data?.field && typeof data?.failed_event_index === 'number') {
            toast({
              title: `Event ${data.failed_event_index + 1}: ${data.field}`,
              description: data.reason || 'Validation error',
              variant: 'destructive',
            });
          }
          console.log('API save failed, using fallback:', apiError.message);
          setApiAvailable(false);
          shouldUseFallback = true;
        }
      }
      
      if (shouldUseFallback) {
        setApiAvailable(false);
        // Fallback to user profile
        const currentEvents = contextUser.events || {};
        currentEvents[tenantId] = events;
        
        await User.updateMyUserData({ events: currentEvents });
        
        toast({
          title: t('eventsSaved'),
          description: t('eventScheduleUpdatedLocal'),
        });
      }
    } catch (error) {
      console.error('Error saving events:', error);
      const data = error?.response?.data || error?.data || {};
      if (data?.field && typeof data?.failed_event_index === 'number') {
        toast({
          title: `Event ${data.failed_event_index + 1}: ${data.field}`,
          description: data.reason || 'Validation error',
          variant: 'destructive',
        });
        return;
      }
      toast({
        title: t('saveFailed'),
        description: data.reason || error.message || t('failedToSaveEvents'),
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const addEvent = () => {
    setEvents(prev => [...prev, {
      event_name: '',
      city: '',
      venue: '',
      start_date: '',
      end_date: '',
      timezone: 'America/New_York',
      availability_link: '',
      booth_number: '',
      talk_link: '',
      pre_window_days: 14,
      post_window_days: 7,
      priority: 1,
      country: [],
      use_custom_playbook: false,
      use_native_language: false,
      custom_touches: [],
      custom_touches_by_type: {},
    }]);
  };

  const updateEvent = (index, field, value) => {
    setEvents(prev => prev.map((evt, i) => 
      i === index ? { ...evt, [field]: value } : evt
    ));
  };

  const patchEvent = (index, patch) => {
    setEvents((prev) => prev.map((evt, i) => (i === index ? { ...evt, ...patch } : evt)));
  };

  const removeEvent = (index) => {
    setEvents(prev => prev.filter((_, i) => i !== index));
  };

  const toggleExpanded = (index) => {
    setExpandedEvents(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  if (!contextUser) {
    return (
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-[#00C2C2]" />
            {t('eventsAndConferences')}
          </CardTitle>
          <CardDescription>{t('trackEventsAndConferences')}</CardDescription>
        </CardHeader>
        <CardContent className="min-h-[400px] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#00C2C2]" />
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-[#00C2C2]" />
            {t('eventsAndConferences')}
          </CardTitle>
          <CardDescription>{t('trackEventsAndConferences')}</CardDescription>
        </CardHeader>
        <CardContent className="min-h-[400px] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#00C2C2]" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-md">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-[#00C2C2]" />
          {t('eventsAndConferences')}
        </CardTitle>
        <CardDescription>
          {t('trackEventsAndConferences')}
          {!apiAvailable && (
            <span className="block mt-1 text-xs text-yellow-700">
              {t('usingLocalStorageApiUnavailable')}
              {apiUnavailableReason?.status ? ` — HTTP ${apiUnavailableReason.status}` : ''}
              {apiUnavailableReason?.message ? `: ${apiUnavailableReason.message}` : ''})
              {' '}
              <button
                type="button"
                onClick={() => { setApiAvailable(true); setApiUnavailableReason(null); loadEvents(); }}
                className="underline text-yellow-800 hover:text-yellow-900 ml-1"
              >
                {t('retry')}
              </button>
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="min-h-[400px]">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">{t('myEvents')}</CardTitle>
                <CardDescription>{t('addConferencesTradeShows')}</CardDescription>
              </div>
              <Button onClick={addEvent} variant="outline" size="sm">
                <Plus className="w-4 h-4 mr-1" />
                {t('addEvent')}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {events.length === 0 ? (
              <div className="text-center py-8 text-[#5C6B7A]">
                <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">{t('noEventsAddedYet')}</p>
                <p className="text-xs mt-1">{t('addEventsToEnableEventMessaging')}</p>
              </div>
            ) : (
              events.map((event, index) => {
                const startDay = eventDateInputValue(event.start_date);
                const endDateMin =
                  startDay && startDay >= todayMin ? startDay : todayMin;
                return (
                <Card key={index} className="relative">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-base">
                          {event.event_name || `${t('event')} ${index + 1}`}
                        </CardTitle>
                        {event.start_date && (
                          <CardDescription>
                            {new Date(event.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            {event.city && ` • ${event.city}`}
                          </CardDescription>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleExpanded(index)}
                        >
                          {expandedEvents[index] ? t('collapse') : t('expand')}
                          {expandedEvents[index] ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />}
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => removeEvent(index)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  
                  {(expandedEvents[index] ||
                    Object.keys(getEventErrors(event)).length > 0) && (
                    <>
                      <CardContent className="space-y-4">
                        {/* Basic Info */}
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <Label>{t('eventName')} <span className="text-red-500">*</span></Label>
                            <Input
                              placeholder={t('eventNameExample')}
                              value={event.event_name || ''}
                              onChange={(e) => updateEvent(index, 'event_name', e.target.value)}
                              required
                              aria-invalid={!!getEventErrors(event).event_name}
                            />
                            {getEventErrors(event).event_name && (
                              <p className="text-xs text-red-500 mt-1">{getEventErrors(event).event_name}</p>
                            )}
                          </div>
                          <div>
                            <Label>{t('city')} <span className="text-red-500">*</span></Label>
                            <Input
                              placeholder={t('cityExample')}
                              value={event.city || ''}
                              onChange={(e) => updateEvent(index, 'city', e.target.value)}
                              required
                              aria-invalid={!!getEventErrors(event).city}
                            />
                            {getEventErrors(event).city && (
                              <p className="text-xs text-red-500 mt-1">{getEventErrors(event).city}</p>
                            )}
                          </div>
                        </div>

                        <div>
                          <Label>{t('venue')} <span className="text-red-500">*</span></Label>
                          <Input
                            placeholder={t('venueExample')}
                            value={event.venue || ''}
                            onChange={(e) => updateEvent(index, 'venue', e.target.value)}
                            required
                            aria-invalid={!!getEventErrors(event).venue}
                          />
                          {getEventErrors(event).venue && (
                            <p className="text-xs text-red-500 mt-1">{getEventErrors(event).venue}</p>
                          )}
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <Label>{t('startDate')} <span className="text-red-500">*</span></Label>
                            <Input
                              type="date"
                              min={todayMin}
                              value={event.start_date?.split('T')[0] || ''}
                              onChange={(e) => updateEvent(index, 'start_date', e.target.value ? `${e.target.value}T00:00:00Z` : '')}
                              required
                              aria-invalid={!!getEventErrors(event).start_date}
                            />
                            {getEventErrors(event).start_date && (
                              <p className="text-xs text-red-500 mt-1">{getEventErrors(event).start_date}</p>
                            )}
                          </div>
                          <div>
                            <Label>{t('endDate')} <span className="text-red-500">*</span></Label>
                            <Input
                              type="date"
                              min={endDateMin}
                              value={event.end_date?.split('T')[0] || ''}
                              onChange={(e) => updateEvent(index, 'end_date', e.target.value ? `${e.target.value}T23:59:59Z` : '')}
                              required
                              aria-invalid={!!getEventErrors(event).end_date}
                            />
                            {getEventErrors(event).end_date && (
                              <p className="text-xs text-red-500 mt-1">{getEventErrors(event).end_date}</p>
                            )}
                          </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <Label>{t('boothNumber')}</Label>
                            <Input
                              placeholder={t('boothNumberExample')}
                              value={event.booth_number || ''}
                              onChange={(e) => updateEvent(index, 'booth_number', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label>{t('availabilityLink')}</Label>
                            <Input
                              type="url"
                              placeholder={t('availabilityLinkPlaceholder')}
                              value={event.availability_link || ''}
                              onChange={(e) => updateEvent(index, 'availability_link', e.target.value)}
                            />
                          </div>
                        </div>

                        <div>
                          <Label>{t('meetingOffer')}</Label>
                          <Input
                            placeholder={t('meetingOfferExample')}
                            value={event.meeting_offer || ''}
                            onChange={(e) => updateEvent(index, 'meeting_offer', e.target.value)}
                          />
                        </div>
                      </CardContent>

                      {/* Messaging Guidelines */}
                      <CardHeader className="pt-0">
                        <CardTitle className="text-base">{t('messagingGuidelines')}</CardTitle>
                        <CardDescription>
                          {t('customInstructionsForAi')}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <Label>{t('preEventGuidelines')}</Label>
                          <Textarea
                            placeholder={t('preEventGuidelinesExample')}
                            value={event.pre_event_guidelines || ''}
                            onChange={(e) => updateEvent(index, 'pre_event_guidelines', e.target.value)}
                            rows={3}
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            {t('infoToIncludeBeforeEvent')}
                          </p>
                        </div>
                        
                        <div>
                          <Label>{t('duringEventGuidelines')}</Label>
                          <Textarea
                            placeholder={t('duringEventGuidelinesExample')}
                            value={event.during_event_guidelines || ''}
                            onChange={(e) => updateEvent(index, 'during_event_guidelines', e.target.value)}
                            rows={3}
                          />
                        </div>
                        
                        <div>
                          <Label>{t('postEventGuidelines')}</Label>
                          <Textarea
                            placeholder={t('postEventGuidelinesExample')}
                            value={event.post_event_guidelines || ''}
                            onChange={(e) => updateEvent(index, 'post_event_guidelines', e.target.value)}
                            rows={3}
                          />
                        </div>
                      </CardContent>

                      {/* Session Details */}
                      <CardHeader className="pt-0">
                        <CardTitle className="text-base">{t('sessionDetailsOptional')}</CardTitle>
                        <CardDescription>
                          {t('ifSpeakingOrWorkshop')}
                          {' '}
                          {t('sessionDetailsSessionInviteNote')}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <Label>{t('sessionTitle')}</Label>
                          <Input
                            placeholder={t('sessionTitleExample')}
                            value={event.session_title || ''}
                            onChange={(e) => updateEvent(index, 'session_title', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>{t('sessionDescription')}</Label>
                          <Textarea
                            placeholder={t('sessionDescriptionPlaceholder')}
                            value={event.session_description || ''}
                            onChange={(e) => updateEvent(index, 'session_description', e.target.value)}
                            rows={2}
                          />
                        </div>
                      </CardContent>

                      {/* Post-Event Assets */}
                      <CardHeader className="pt-0">
                        <CardTitle className="text-base">{t('postEventAssets')}</CardTitle>
                        <CardDescription>{t('linksToShareFollowUp')}</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <Label>{t('sessionRecordingLink')}</Label>
                          <Input
                            type="url"
                            placeholder={t('urlPlaceholder')}
                            value={event.session_recording_link || ''}
                            onChange={(e) => updateEvent(index, 'session_recording_link', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>{t('slidesLink')}</Label>
                          <Input
                            type="url"
                            placeholder={t('urlPlaceholder')}
                            value={event.slides_link || ''}
                            onChange={(e) => updateEvent(index, 'slides_link', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>{t('postEventResourceLink')}</Label>
                          <Input
                            type="url"
                            placeholder={t('urlPlaceholder')}
                            value={event.post_event_resource_link || ''}
                            onChange={(e) => updateEvent(index, 'post_event_resource_link', e.target.value)}
                          />
                        </div>
                      </CardContent>

                      {/* Custom Playbook Section */}
                      <CardHeader className="pt-0">
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle className="text-base flex items-center gap-2">
                              <Sparkles className="h-4 w-4 text-purple-500" />
                              {t('messageSequence')}
                            </CardTitle>
                            <CardDescription>
                              {t('defineTouchesOutreachSequence')}
                            </CardDescription>
                          </div>
                          <div className="flex items-center gap-3">
                            <Label className="text-sm">{t('customPlaybook')}</Label>
                            <Switch
                              checked={event.use_custom_playbook || false}
                              onCheckedChange={(checked) => {
                                updateEvent(index, 'use_custom_playbook', checked);
                              }}
                            />
                          </div>
                        </div>
                      </CardHeader>
                      
                      <CardContent className="space-y-4">
                        {!event.use_custom_playbook ? (
                          <div className="text-center py-6 border border-dashed rounded-lg">
                            <p className="text-gray-500 text-sm mb-3">
                              {t('usingDefaultPlaybook')}
                            </p>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => updateEvent(index, 'use_custom_playbook', true)}
                            >
                              <Plus className="h-4 w-4 mr-1" />
                              {t('createCustomSequence')}
                            </Button>
                          </div>
                        ) : (
                          <EventCustomPlaybookBuilder
                            key={event.id || `event-${index}-${event.event_name || ''}`}
                            event={event}
                            onPatchEvent={(patch) => patchEvent(index, patch)}
                            productLinkOptions={productLinkOptions}
                            t={t}
                          />
                        )}
                      </CardContent>

                      {/* Target Region */}
                      <CardHeader className="pt-0">
                        <CardTitle className="text-base">{t('targetRegion')}</CardTitle>
                      </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label>{t('targetCountries')}</Label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const currentCountries = Array.isArray(event.country) ? event.country : [];
                            const isAllMode = isAllCountriesMode(currentCountries);
                            
                            if (isAllMode) {
                              // Currently "All" - switch to no selection
                              updateEvent(index, 'country', []);
                            } else {
                              // Not "All" - switch to ALL mode (no restrictions)
                              updateEvent(index, 'country', ['ALL']);
                            }
                          }}
                          className="text-xs h-7"
                        >
                          {isAllCountriesMode(Array.isArray(event.country) ? event.country : [])
                            ? t('deselectAll')
                            : t('selectAllNoRestrictions')}
                        </Button>
                      </div>
                      
                      {/* Show info message when ALL is selected */}
                      {isAllCountriesMode(Array.isArray(event.country) ? event.country : []) && (
                        <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-3">
                          <p className="text-sm text-green-800">
                            <strong>{t('allCountriesSelectedStrong')}</strong> — {t('noGeoRestrictions')}
                            {' '}
                            {t('personasAnyCountryIncluded')}
                          </p>
                        </div>
                      )}
                      
                      <div
                        className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2 max-h-[200px] overflow-y-auto"
                        style={{
                          gridAutoFlow: 'column',
                          gridTemplateRows: `repeat(${countryGridRows}, auto)`,
                        }}
                      >
                        {SORTED_COUNTRIES.map(c => {
                          const countries = Array.isArray(event.country) ? event.country : [];
                          const isAllMode = isAllCountriesMode(countries);
                          const isChecked = isAllMode || countries.includes(c.value);
                          
                          return (
                            <Label key={c.value} className="flex items-center space-x-2 text-sm cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) => {
                                  const currentCountries = Array.isArray(event.country) ? event.country : [];
                                  const wasAllMode = isAllCountriesMode(currentCountries);
                                  
                                  if (wasAllMode) {
                                    // Switching from ALL mode - if unchecking, expand to all countries minus this one
                                    if (!e.target.checked) {
                                      const allExceptThis = COUNTRIES
                                        .map(country => country.value)
                                        .filter(v => v !== c.value);
                                      updateEvent(index, 'country', allExceptThis);
                                    }
                                    // If checking while in ALL mode, do nothing (already included)
                                  } else {
                                    // Normal mode - add or remove country
                                    const updated = e.target.checked
                                      ? [...currentCountries, c.value]
                                      : currentCountries.filter(country => country !== c.value);
                                    
                                    // If all countries are now selected, switch to ALL mode
                                    if (e.target.checked && updated.length === COUNTRIES.length) {
                                      updateEvent(index, 'country', ['ALL']);
                                    } else {
                                      updateEvent(index, 'country', updated);
                                    }
                                  }
                                }}
                                className="rounded border-gray-300"
                              />
                              <span>{c.label}</span>
                            </Label>
                          );
                        })}
                      </div>
                    </div>

                    {/* [2026-10-05] Native-language option. Sits with the country
                        choice because it is a statement about the recipients, not
                        about the sequence. Default OFF — before this, every event
                        message went out in English whatever the recipient's country. */}
                    <div className="border-t pt-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <Label className="text-sm font-medium">
                            {t('writeInNativeLanguage')}
                          </Label>
                          <p className="text-xs text-gray-500 mt-1">
                            {t('writeInNativeLanguageHelp')}
                          </p>
                        </div>
                        <Switch
                          checked={event.use_native_language === true}
                          onCheckedChange={(checked) => {
                            updateEvent(index, 'use_native_language', checked);
                          }}
                        />
                      </div>
                    </div>
                  </CardContent>
                    </>
                  )}
                </Card>
              );
              })
            )}
          </CardContent>
        </Card>

        <div className="mt-4">
          <Button 
            onClick={handleSave} 
            disabled={isSaving || hasAnyErrors}
            title={hasAnyErrors ? t('fixRequiredFieldsBeforeSaving') : ''}
            className="w-full bg-[#00C2C2] hover:bg-[#00A3A3]"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('saving')}
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {t('saveEvents')}
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}